import get from 'lodash/get';
import genererRecommandation from '@/services/recommandation/genererRecommandation';
import { getAdresseByGescomAdresse } from '@/services/general/adresseManager';
import * as recomConfig from '@/configurations/recomConfig';
import { transformProduitToPanneau, updateDataBudgetGenerationByBudgets } from '@/services/recommandation/panneauManager';
import { getIconPanneau } from '@/services/gestionIconMarker/iconColorGetter';
import {
  isLigneLocation,
  isLigneTlpe,
  isLigneHonoraires,
  isLigneFraisTechnique,
  isLigneHonoraireFraisTraitement,
} from '@/services/recommandation/gestionEcheancier';
import genererGuid from '@/services/general/genererGuid';
import { calcPrixTotal } from '@/services/general/calculDataLigne';
import { genererBudget } from '@/services/recommandation/genererPanneau';
import calculArrondi from '@/services/general/calculArrondi';
import { calculNetBudget } from '@/services/recommandation/calculRemisePanneau';

function getPointDeVenteFromDevis(devis) {
  const intervenantAnnonceur = devis.intervenants.find(i => i.annonceur);
  const intervenantPayeur = devis.intervenants.find(i => i.payeur);
  const enseigne = get(intervenantPayeur, 'tiers', null);
  const adresse = get(intervenantAnnonceur, 'tiers.adressePostale', null);
  const pointDeVente = {
    ...get(intervenantAnnonceur, 'tiers', {}),
    iconMarker: null,
    tiersParent: enseigne,
    tiersParentId: get(enseigne, 'id'),
    intervenantId: get(intervenantAnnonceur, 'id'),
    urlLogo: get(intervenantAnnonceur, 'tiersLogoUrl') ?? get(intervenantPayeur, 'tiersLogoUrl'),
    adressePostale: getAdresseByGescomAdresse(adresse),
  };
  return pointDeVente;
}

function assignGuidsToPanneaux(lignesCommande) {
  const guidByProduitId = new Map();
  for (const ligne of lignesCommande) {
    if (ligne.type !== recomConfig.REGROUPEMENT) continue;
    if (ligne.produit && ligne.produit.guid) continue;
    const produitId = ligne.produit && ligne.produit.id;

    if (!produitId) continue;
    if (!guidByProduitId.has(produitId)) {
      guidByProduitId.set(produitId, genererGuid());
    }
    ligne.produit.guid = guidByProduitId.get(produitId);
  }
}

export function lignesCommandeToPanneaux(lignesCommande, societe) {

  assignGuidsToPanneaux(lignesCommande);

  const panneauxMap = new Map();
  for (const ligne of lignesCommande) {
    if (ligne.type !== recomConfig.REGROUPEMENT) continue;
    const guid = ligne.produit && ligne.produit.guid;
    if (!guid) continue;
    if (!panneauxMap.has(guid)) {
      ligne.produit.urlLogo = ligne.produitLogoUrl;
      const location = ligne.lignesCommande.find(isLigneLocation);
      const fournisseur = ligne.fournisseur || (location && location.fournisseur);
      panneauxMap.set(guid, {
        panneau: transformProduitToPanneau(ligne.produit, fournisseur),
        blocs: [],
      });
    }
    panneauxMap.get(guid).blocs.push(ligne);
  }
  let numPanneau = 1;
  return Array.from(panneauxMap.values()).map(({ panneau, blocs }) => {
    panneau.actif = blocs[0] && blocs[0].etatObtenu !== recomConfig.SANS_SUITE;
    if (panneau.actif) {
      panneau.num = numPanneau;
      numPanneau = numPanneau + 1;
    }

    panneau.budgets = blocs.map(ligneRegroupement => {
      const budget = genererBudget();
      budget.guid = ligneRegroupement.guid || budget.guid;
      budget.ligneCommandeId = ligneRegroupement.id;
      const lignesCommande = ligneRegroupement.lignesCommande || [];
      for (const ligne of lignesCommande) {
        if (ligne.type !== recomConfig.NORMAL) continue;

        if (isLigneLocation(ligne)) {
          budget.dateDebut = ligne.dateDebut ? new Date(ligne.dateDebut) : null;
          budget.dateFin = ligne.dateFin ? new Date(ligne.dateFin) : null;
          budget.prixUnitaireBrut = ligne.prixUnitaire;
          budget.quantiteBrut = ligne.quantite != null ? ligne.quantite : 1;
          budget.uniteQteBrut = ligne.uniteVente || recomConfig.ANNEE;
          budget.prixBrutNegocie = calculArrondi(ligne.prixUnitaire * budget.quantiteBrut, 5);
          budget.ligneCommandeIds.location = ligne.id;
          budget.remise.remise = ligne.remise;
          panneau.categorie = {
            id: ligne.produit.id,
            libelle: ligne.produit.libelle,
            couleur: ligne.produit.couleurHtmlRgb,
          };
          panneau.icon = getIconPanneau(panneau);
          calculNetBudget(budget);
          continue;
        }
        if (isLigneTlpe(ligne, societe)) {
          budget.prixUnitaireTlpe = ligne.prixUnitaire;
          budget.quantiteTlpe = ligne.quantite != null ? ligne.quantite : 1;
          budget.uniteQteTlpe = ligne.uniteVente || recomConfig.ANNEE;
          budget.tlpe = calculArrondi(ligne.prixUnitaire * budget.quantiteTlpe, 5);
          budget.ligneCommandeIds.tlpe = ligne.id;
          continue;
        }
        if (isLigneHonoraires(ligne, societe, true)) {
          budget.honoraires = ligne.prixUnitaire;
          budget.ligneCommandeIds.honoraires = ligne.id;
          budget.modeCalculHonoraire = ligne.modeCalculHonoraire;
          continue;
        }
        if (isLigneFraisTechnique(ligne, societe)) {
          budget.fraisTechnique = ligne.prixUnitaire;
          budget.ligneCommandeIds.fraisTechnique = ligne.id;
          continue;
        }
        if (isLigneHonoraireFraisTraitement(ligne, societe)) {
          budget.honoraireFraisTraitement = ligne.prixUnitaire;
          budget.ligneCommandeIds.honoraireFraisTraitement = ligne.id;
          continue;
        }
      }

      const ligneCommentaire = lignesCommande.find(l => l.type === recomConfig.COMMENTAIRE);
      if (ligneCommentaire) panneau.commentaire = ligneCommentaire.libelle;

      budget.prixTotal = calcPrixTotal(budget);
      return budget;
    });
    updateDataBudgetGenerationByBudgets(panneau.budgets, panneau.budgetGeneration);
    return panneau;
  });
}

function getLigneHonoraireForfaitaire(lignesCommande, societe) {
  return lignesCommande.find(lc => lc.type === recomConfig.NORMAL
    && lc.typeProduit === recomConfig.CATEGORIE_DIVERS
    && lc.produitId === societe.produitIdHonoraireForfaitaire);
}

export function recommandationMapping(devis) {
  const pointDeVente = getPointDeVenteFromDevis(devis);
  const { societe, lignesCommande } = devis;
  const panneaux = lignesCommandeToPanneaux(lignesCommande, societe);
  const ligneHonoraireForfaitaire = getLigneHonoraireForfaitaire(lignesCommande, societe);
  const recommandation = {
    ...genererRecommandation(),
    pointDeVente,
    panneaux,
    theme: devis.theme,
    id: devis.id,
    etat: devis.etat,
    commentaire: devis.commentaire,
    commercial: devis.commercial,
    commercialId: devis.commercialId,
    dateCreation: devis.creationTime,
    societe: { ...societe, urlLogo: devis.societeLogoUrl },
    honoraireForfaitaire: ligneHonoraireForfaitaire ? ligneHonoraireForfaitaire.prixUnitaire : null,
    ligneCommandeHonoraireForfaitaireId: ligneHonoraireForfaitaire ? ligneHonoraireForfaitaire.id : null,
  };
  return recommandation;
}
